import React, { useState, useRef, useEffect } from 'react';

/**
 * AfkimAIChat - צ'אט עם מוח AI עסקי
 * Floating chat component for AI business assistant
 */
const AfkimAIChat = ({ externalOpen, onOpenChange, hideFloatingButton = false }) => {
  const [internalOpen, setInternalOpen] = useState(false);

  // Support both internal and external control
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setIsOpen = (value) => {
    if (onOpenChange) {
      onOpenChange(value);
    } else {
      setInternalOpen(value);
    }
  };
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId] = useState(() => `conv_${Date.now()}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Use backend proxy to avoid CORS issues when accessing from external URLs
  const AI_SERVER_URL = window.location.origin.includes('localhost')
    ? 'http://localhost:3000'
    : window.location.origin;

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Add welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        type: 'ai',
        text: 'שלום! 👋 אני אפיקים AI - המוח העסקי שלך.\n\nאני יכול לעזור לך עם:\n• סיכומים וניתוחים עסקיים\n• מידע על לקוחות והזמנות\n• תובנות והמלצות\n\nמה תרצה לדעת?',
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: `user_${Date.now()}`,
      type: 'user',
      text: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${AI_SERVER_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage.text,
          conversation_id: conversationId
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      const aiMessage = {
        id: `ai_${Date.now()}`,
        type: 'ai',
        text: data.answer,
        recommendations: data.recommendations,
        data: data.data,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);

    } catch (error) {
      console.error('Error sending message:', error);

      const errorMessage = {
        id: `error_${Date.now()}`,
        type: 'ai',
        text: 'מצטער, יש בעיה בחיבור לשרת AI. אנא נסה שוב מאוחר יותר.',
        isError: true,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Quick actions
  const quickActions = [
    { label: 'מה המצב היום?', query: 'מה המצב היום?' },
    { label: 'סיכום מכירות', query: 'תן לי סיכום מכירות של החודש' },
    { label: 'לקוחות פעילים', query: 'כמה לקוחות פעילים יש לנו?' }
  ];

  const handleQuickAction = (query) => {
    setInputValue(query);
    setTimeout(() => sendMessage(), 100);
  };

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: '100px',
            left: '20px',
            width: '380px',
            height: '550px',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 9999,
            fontFamily: "'Segoe UI', 'Rubik', sans-serif",
            direction: 'rtl'
          }}
        >
          {/* Header */}
          <div
            style={{
              background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img
                src="/afikim_ai.jpeg"
                alt="Afikim AI"
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.3)'
                }}
              />
              <div>
                <div style={{ color: '#fff', fontWeight: '600', fontSize: '16px' }}>
                  אפיקים AI
                </div>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
                  מוח עסקי חכם
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: '#fff',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
              onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
            >
              ✕
            </button>
          </div>

          {/* Messages Area */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              backgroundColor: '#f5f7fb',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}
          >
            {messages.map((message) => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: message.type === 'user' ? 'flex-start' : 'flex-end'
                }}
              >
                <div
                  style={{
                    maxWidth: '85%',
                    padding: '12px 16px',
                    borderRadius: message.type === 'user'
                      ? '18px 18px 4px 18px'
                      : '18px 18px 18px 4px',
                    backgroundColor: message.type === 'user'
                      ? '#1e88e5'
                      : message.isError
                        ? '#ffebee'
                        : '#fff',
                    color: message.type === 'user' ? '#fff' : '#333',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.5',
                    fontSize: '14px'
                  }}
                >
                  {message.text}

                  {/* Recommendations */}
                  {message.recommendations && message.recommendations.length > 0 && (
                    <div
                      style={{
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: '1px solid #eee'
                      }}
                    >
                      <div style={{ fontWeight: '600', marginBottom: '8px', color: '#1565c0' }}>
                        💡 המלצות:
                      </div>
                      {message.recommendations.map((rec, idx) => (
                        <div
                          key={idx}
                          style={{
                            fontSize: '13px',
                            color: '#555',
                            padding: '4px 0',
                            paddingRight: '12px',
                            position: 'relative'
                          }}
                        >
                          <span style={{ position: 'absolute', right: 0 }}>→</span>
                          {rec}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: '#999',
                    marginTop: '4px',
                    padding: '0 8px'
                  }}
                >
                  {formatTime(message.timestamp)}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  padding: '8px'
                }}
              >
                <div
                  style={{
                    backgroundColor: '#fff',
                    padding: '12px 20px',
                    borderRadius: '18px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#1e88e5',
                          animation: `bounce 1.4s infinite ease-in-out both`,
                          animationDelay: `${i * 0.16}s`
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length <= 1 && !isLoading && (
            <div
              style={{
                padding: '8px 16px',
                backgroundColor: '#fff',
                borderTop: '1px solid #eee',
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap'
              }}
            >
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickAction(action.query)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '16px',
                    border: '1px solid #1e88e5',
                    backgroundColor: '#fff',
                    color: '#1e88e5',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#1e88e5';
                    e.target.style.color = '#fff';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#fff';
                    e.target.style.color = '#1e88e5';
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}

          {/* Input Area */}
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fff',
              borderTop: '1px solid #eee',
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="שאל אותי משהו..."
              disabled={isLoading}
              style={{
                flex: 1,
                padding: '12px 16px',
                borderRadius: '24px',
                border: '1px solid #ddd',
                fontSize: '14px',
                outline: 'none',
                direction: 'rtl',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#1e88e5'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
            <button
              onClick={sendMessage}
              disabled={!inputValue.trim() || isLoading}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: inputValue.trim() && !isLoading ? '#1e88e5' : '#ccc',
                color: '#fff',
                cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                transition: 'background-color 0.2s',
                transform: 'rotate(180deg)'
              }}
            >
              ➤
            </button>
          </div>
        </div>
      )}

      {/* Floating Button - Hidden when controlled externally */}
      {!hideFloatingButton && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            width: '65px',
            height: '65px',
            borderRadius: '50%',
            background: 'white',
            border: 'none',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.25)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            zIndex: 9998,
            padding: '0',
            overflow: 'hidden'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(30, 136, 229, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.25)';
          }}
          title="אפיקים AI - צ'אט עסקי חכם"
        >
          {isOpen ? (
            <span style={{ fontSize: '24px', color: '#1e88e5' }}>✕</span>
          ) : (
            <img
              src="/afikim_ai.jpeg"
              alt="Afikim AI"
              style={{
                width: '55px',
                height: '55px',
                borderRadius: '50%',
                objectFit: 'cover'
              }}
            />
          )}
        </button>
      )}

      {/* CSS Animation */}
      <style>
        {`
          @keyframes bounce {
            0%, 80%, 100% {
              transform: scale(0);
            }
            40% {
              transform: scale(1);
            }
          }
        `}
      </style>
    </>
  );
};

export default AfkimAIChat;
